import React from 'react';
import { Card, CardContent } from './ui/card';
import { Target, Award, Shield, Users } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Target,
      title: 'Client Focus',
      description: 'We prioritize understanding our clients\' unique needs and objectives, tailoring our services to deliver effective and practical solutions.'
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'We strive for excellence in every aspect of our work, maintaining the highest standards of professionalism and legal expertise.'
    },
    {
      icon: Shield,
      title: 'Integrity',
      description: 'We uphold the highest standards of integrity and ethical standards, earning the trust and confidence of our clients.'
    },
    {
      icon: Users,
      title: 'Collaboration',
      description: 'We foster a collaborative environment, both within our team and with our clients, to ensure that we work together effectively to achieve shared goals.'
    }
  ];

  return (
    <section id="about" className="py-24 bg-slate-800/30">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* About Intro */}
          <div className="text-center mb-16 animate-fade-in">
            <div className="h-1 w-20 bg-yellow-500 mx-auto mb-6"></div>
            <p className="text-yellow-500 uppercase tracking-widest text-sm font-semibold mb-4">
              About Us
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              About Soshniti Legal
            </h2>
          </div>

          {/* About Content */}
          <Card className="bg-slate-800/50 border-slate-700 mb-12">
            <CardContent className="p-8 md:p-12">
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                Soshniti Legal is a full-service law firm dedicated to providing comprehensive and high-quality legal solutions to clients across a diverse range of industries and practice areas. With offices in <span className="text-white font-semibold">Pune, Delhi, Mumbai, and UAE</span>, we are strategically positioned to serve clients across India and beyond.
              </p>
              <p className="text-gray-300 text-lg leading-relaxed">
                Our team of experienced attorneys is dedicated to delivering high-quality legal advice and representation. Whether you're a startup navigating the complex legal landscape or an established business seeking expert guidance, Soshniti Legal is here to simplify the process and provide you with the expert guidance you need to succeed.
              </p>
            </CardContent>
          </Card>

          {/* Our Values */}
          <div className="mb-8">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
              Our Core Values
            </h3>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card 
                  key={index}
                  className="bg-slate-800/50 border-slate-700 hover:border-yellow-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-yellow-500/10 group animate-slide-up"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <CardContent className="p-6 space-y-4">
                    <div className="flex items-start gap-4">
                      <div className="bg-yellow-500 p-3 rounded-lg transition-transform duration-300 group-hover:scale-110 flex-shrink-0">
                        <Icon className="h-6 w-6 text-slate-900" />
                      </div>
                      <div>
                        <h4 className="text-xl font-bold text-white mb-3 group-hover:text-yellow-500 transition-colors duration-300">
                          {value.title}
                        </h4>
                        <p className="text-gray-300 leading-relaxed">
                          {value.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Why Choose Us */}
          <Card className="bg-slate-800/50 border-slate-700 mt-12">
            <CardContent className="p-8 md:p-12">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-6">
                Why Choose Soshniti Legal?
              </h3>
              <div className="space-y-4 text-gray-300 leading-relaxed">
                <div>
                  <h4 className="text-white font-semibold mb-2">Deep Understanding of Startups</h4>
                  <p>We understand the unique challenges and opportunities faced by startups.</p>
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-2">Tailored Legal Solutions</h4>
                  <p>We provide customized legal solutions to meet your specific needs.</p>
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-2">Experienced Legal Team</h4>
                  <p>Our team of experienced attorneys has a proven track record in advising startups.</p>
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-2">Cost-Effective Services</h4>
                  <p>We offer competitive pricing without compromising on quality.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default About;
